import React, { useEffect, useState } from "react";

const TYPES = [
    { value: "percentage", label: "Percentage", unit: "%" },
    { value: "flat", label: "Flat", unit: "₹" },
    { value: "per_sqft", label: "Per Sq Ft", unit: "₹ / sq ft" },
];

const round2 = (n) => Math.round(n * 100) / 100;
const blockInvalidKeys = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};

const CommissionSlabModal = ({
    onClose,
    onSave,
    formData,
    setFormData,
    loading,
    canAdd,
    isEdit,
}) => {
    const [submitted, setSubmitted] = useState(false);

    // Keep split.type_of_split mirrored to amount_type at all times
    useEffect(() => {
        if (formData.split.type_of_split !== formData.amount_type) {
            setFormData((prev) => ({
                ...prev,
                split: { ...prev.split, type_of_split: prev.amount_type },
            }));
        }
    }, [formData.amount_type]);

    const isPercentage = formData.amount_type === "percentage";
    const typeMeta = TYPES.find((t) => t.value === formData.amount_type) || TYPES[0];
    const unit = typeMeta.unit;

    // ---------- handlers ----------
    const handleTypeChange = (value) =>
        setFormData((prev) => ({
            ...prev,
            amount_type: value,
            // percentage can never exceed 100
            base_amount: value === "percentage" && Number(prev.base_amount) > 100 ? "100" : prev.base_amount,
            split: { ...prev.split, type_of_split: value, admin: "", franchise_manager: "" },
        }));

    const handleBaseChange = (raw) =>
        setFormData((prev) => {
            let val = raw;
            if (val !== "" && Number(val) < 0) val = "0";
            if (prev.amount_type === "percentage" && Number(val) > 100) val = "100";

            // keep split in sync when amount changes
            const base = Number(val) || 0;
            const hasAdmin = prev.split.admin !== "";
            const adminNum = hasAdmin ? Math.min(Number(prev.split.admin), base) : 0;

            return {
                ...prev,
                base_amount: val,
                split: {
                    ...prev.split,
                    admin: hasAdmin ? String(adminNum) : "",
                    franchise_manager: hasAdmin ? String(round2(base - adminNum)) : "",
                },
            };
        });

    const updateSplit = (key, rawValue) => {
        setFormData((prev) => {
            // splitBase is always relative to base_amount, whatever the type
            const splitBase = Number(prev.base_amount) || 0;
            const otherKey = key === "admin" ? "franchise_manager" : "admin";

            let val = rawValue;
            if (val !== "" && Number(val) < 0) val = "0";
            if (val !== "" && Number(val) > splitBase) val = String(splitBase);

            const otherValue = val === "" ? "" : String(round2(splitBase - Number(val)));

            return {
                ...prev,
                split: { ...prev.split, [key]: val, [otherKey]: otherValue },
            };
        });
    };

    // ---------- validation ----------
    const base = Number(formData.base_amount);
    const adminVal = formData.split.admin;
    const fmVal = formData.split.franchise_manager;
    const errors = {};

    if (formData.base_amount === "") errors.base = "Amount is required";
    else if (base <= 0) errors.base = "Amount must be greater than 0";
    else if (isPercentage && base > 100) errors.base = "Percentage cannot exceed 100";

    if (!errors.base) {
        if (adminVal === "") errors.admin = "Admin share is required";
        if (fmVal === "") errors.fm = "Franchise manager share is required";
        if (!errors.admin && !errors.fm && round2(Number(adminVal) + Number(fmVal)) !== round2(base)) {
            errors.split = `Admin + Franchise Manager must equal ${base} ${unit}`;
        }
    }

    if (formData.is_gst_excluded) {
        if (formData.gst_percentage === "" || formData.gst_percentage === undefined) errors.gst = "GST % is required";
        else if (Number(formData.gst_percentage) < 0 || Number(formData.gst_percentage) > 100)
            errors.gst = "GST must be between 0 and 100";
    }

    const isValid = Object.keys(errors).length === 0;
    // required errors show only after a save attempt; range errors show live
    const show = (key, field) => errors[key] && (submitted || field !== "");

    const handleSave = () => {
        setSubmitted(true);
        if (!isValid) return;
        onSave();
    };

    // ---------- styles ----------
    const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };
    const inputStyle = (hasError) => ({
        width: "100%",
        padding: "10px 48px 10px 12px",
        borderRadius: "8px",
        border: `1px solid ${hasError ? "#ef4444" : "#e5e7eb"}`,
        outline: "none",
        boxSizing: "border-box",
        background: "white",
    });
    const suffixStyle = {
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: "12px",
        fontWeight: 600,
        color: "#6b7280",
        pointerEvents: "none",
    };
    const errorStyle = { color: "#ef4444", fontSize: "12px", marginTop: "4px" };

    const adminShare = base > 0 && adminVal !== "" ? Math.min((Number(adminVal) / base) * 100, 100) : 0;
    const fmShare = base > 0 && fmVal !== "" ? Math.min((Number(fmVal) / base) * 100, 100) : 0;
    const splitDisabled = !base || base <= 0;

    return (
        <div className="localities-popup-overlay" onClick={onClose}>
            <div
                className="localities-popup-content"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "560px" }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                    }}
                >
                    <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600", color: "black" }}>
                        Commission Slab
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: "transparent",
                            border: "none",
                            fontSize: "20px",
                            cursor: "pointer",
                            color: "black",
                            padding: "0",
                        }}
                    >
                        ✕
                    </button>
                </div>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#6b7280" }}>
                        Loading...
                    </div>
                ) : (
                    <>
                        {/* Amount type — segmented toggle */}
                        <div style={{ marginBottom: "16px" }}>
                            <label style={labelStyle}>Amount Type</label>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "4px",
                                    padding: "4px",
                                    background: "#f3f4f6",
                                    borderRadius: "10px",
                                }}
                            >
                                {TYPES.map((t) => {
                                    const active = formData.amount_type === t.value;
                                    return (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => handleTypeChange(t.value)}
                                            style={{
                                                flex: 1,
                                                padding: "8px 0",
                                                border: "none",
                                                borderRadius: "8px",
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                background: active ? "white" : "transparent",
                                                color: active ? "#6b46c1" : "#6b7280",
                                                boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                                transition: "all 0.15s",
                                            }}
                                        >
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Amount */}
                        <div style={{ marginBottom: "20px" }}>
                            <label style={labelStyle}>
                                Amount {isPercentage && <span style={{ fontWeight: 400, color: "#9ca3af" }}>(max 100)</span>}
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="number"
                                    min="0"
                                    max={isPercentage ? 100 : undefined}
                                    value={formData.base_amount}
                                    onKeyDown={blockInvalidKeys}
                                    onWheel={(e) => e.target.blur()}
                                    onChange={(e) => handleBaseChange(e.target.value)}
                                    style={inputStyle(show("base", formData.base_amount))}
                                />
                                <span style={suffixStyle}>{unit}</span>
                            </div>
                            {show("base", formData.base_amount) && <div style={errorStyle}>{errors.base}</div>}
                        </div>

                        {/* Split card */}
                        <div
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: "12px",
                                padding: "16px",
                                marginBottom: "20px",
                                background: "#fafafa",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "12px",
                                }}
                            >
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>Commission Split</span>
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#6b46c1",
                                        background: "#ede9fe",
                                        padding: "3px 10px",
                                        borderRadius: "999px",
                                    }}
                                >
                                    Split by {typeMeta.label}
                                </span>
                            </div>

                            <div style={{ display: "flex", gap: "16px" }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>Admin</label>
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="number"
                                            min="0"
                                            disabled={splitDisabled}
                                            placeholder={splitDisabled ? "Enter amount first" : "0"}
                                            value={formData.split.admin}
                                            onKeyDown={blockInvalidKeys}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => updateSplit("admin", e.target.value)}
                                            style={inputStyle(show("admin", adminVal))}
                                        />
                                        <span style={suffixStyle}>{unit}</span>
                                    </div>
                                    {show("admin", adminVal) && <div style={errorStyle}>{errors.admin}</div>}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelStyle}>Franchise Manager</label>
                                    <div style={{ position: "relative" }}>
                                        <input
                                            type="number"
                                            min="0"
                                            disabled={splitDisabled}
                                            placeholder={splitDisabled ? "Enter amount first" : "0"}
                                            value={formData.split.franchise_manager}
                                            onKeyDown={blockInvalidKeys}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => updateSplit("franchise_manager", e.target.value)}
                                            style={inputStyle(show("fm", fmVal))}
                                        />
                                        <span style={suffixStyle}>{unit}</span>
                                    </div>
                                    {show("fm", fmVal) && <div style={errorStyle}>{errors.fm}</div>}
                                </div>
                            </div>

                            {/* Visual split bar */}
                            <div
                                style={{
                                    display: "flex",
                                    height: "8px",
                                    borderRadius: "999px",
                                    overflow: "hidden",
                                    background: "#e5e7eb",
                                    marginTop: "14px",
                                }}
                            >
                                <div style={{ width: `${adminShare}%`, background: "#8b5cf6", transition: "width 0.2s" }} />
                                <div style={{ width: `${fmShare}%`, background: "#f59e0b", transition: "width 0.2s" }} />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    marginTop: "6px",
                                }}
                            >
                                <span>
                                    <span style={{ color: "#8b5cf6" }}>●</span> Admin {adminShare.toFixed(0)}%
                                </span>
                                <span>
                                    <span style={{ color: "#f59e0b" }}>●</span> Franchise {fmShare.toFixed(0)}%
                                </span>
                            </div>
                            {errors.split && <div style={errorStyle}>{errors.split}</div>}
                            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "8px" }}>
                                Editing one share auto-fills the other so the total equals the amount.
                            </div>
                        </div>

                        {/* GST */}
                        <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <input
                                    id="exclude-gst"
                                    type="checkbox"
                                    checked={formData.is_gst_excluded}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, is_gst_excluded: e.target.checked }))
                                    }
                                    style={{ cursor: "pointer" }}
                                />
                                <label htmlFor="exclude-gst" style={{ fontSize: "14px", color: "#111827", cursor: "pointer" }}>
                                    Exclude GST
                                </label>
                                {formData.is_gst_excluded && (
                                    <div style={{ position: "relative", marginLeft: "8px" }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={formData.gst_percentage}
                                            onKeyDown={blockInvalidKeys}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => {
                                                let val = e.target.value;
                                                if (val !== "" && Number(val) > 100) val = "100";
                                                if (val !== "" && Number(val) < 0) val = "0";
                                                setFormData((prev) => ({ ...prev, gst_percentage: val }));
                                            }}
                                            style={{ ...inputStyle(show("gst", formData.gst_percentage)), width: "90px", padding: "6px 26px 6px 10px" }}
                                        />
                                        <span style={{ ...suffixStyle, right: "8px" }}>%</span>
                                    </div>
                                )}
                            </div>
                            {show("gst", formData.gst_percentage) && <div style={errorStyle}>{errors.gst}</div>}
                        </div>

                        {canAdd && (
                            <button
                                onClick={handleSave}
                                disabled={submitted && !isValid}
                                style={{
                                    width: "100%",
                                    padding: "14px",
                                    border: "1px solid #8b5cf6",
                                    background: submitted && !isValid ? "#f3f4f6" : "white",
                                    color: submitted && !isValid ? "#9ca3af" : "#6b46c1",
                                    borderRadius: "10px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    cursor: submitted && !isValid ? "not-allowed" : "pointer",
                                }}
                            >
                                {isEdit ? "Update Commission Slab" : "Save Commission Slab"}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CommissionSlabModal;